import { q } from './workspace/quote.js';
/** Turn seed variables into env for author scripts: LOST_COMMIT -> SEED_LOST_COMMIT. */
export function seedEnv(seed) {
    const env = {};
    for (const [k, v] of Object.entries(seed))
        env[`SEED_${k}`] = v;
    return env;
}
/**
 * The `@@KEY=value` lines a script printed, with everything else on stdout
 * ignored. Both halves of seeding scrape the same way, because a value is a
 * value whichever step knew it.
 */
function scrapeValues(stdout) {
    const values = {};
    for (const m of stdout.matchAll(/^@@([A-Za-z0-9_]+)=(.*)$/gm))
        values[m[1]] = (m[2] ?? '').trim();
    return values;
}
/**
 * Bring a fresh workspace to the level's starting state on this machine: run
 * the seed script, then the boot step.
 *
 * Both halves are here rather than at each of the six call sites because the
 * one way to get this wrong is silent. A caller that seeded and forgot to
 * boot would produce a workspace with the level's files and none of its
 * processes — which is exactly the browser bug this step exists to close,
 * reintroduced on the driver that was working.
 */
export async function seedWorkspace(ws, mission) {
    if (mission.seedScript === undefined) {
        throw new Error(`mission "${mission.id}" was loaded without its seed script; it cannot be seeded by running one`);
    }
    const r = await ws.runScript(mission.seedScript, { timeoutMs: 90_000 });
    if (r.code !== 0) {
        throw new Error(`seed script for "${mission.id}" failed (exit ${r.code}):\n${r.stderr || r.stdout}`);
    }
    return bootWorkspace(ws, mission, scrapeValues(r.stdout));
}
/**
 * Run the mission's boot step, if it has one, and return the seed values the
 * level is graded against.
 *
 * It runs at the workspace root, as the seed does, with the values already in
 * hand as `SEED_<KEY>` — so a boot script can start a server in the directory
 * a seed value named without being told twice.
 *
 * **A boot script may emit `@@KEY=value` too, and those win.** Anything a
 * seed printed was printed on whichever machine ran the seed, which in the
 * browser is a build machine that has not existed for months; anything boot
 * prints was measured here, a second ago, on the machine the learner is
 * about to type at. A port bound at boot time is the case this exists for
 * (docs/model.md §8, "The port"). A pid is the case it does not: see
 * docs/missions.md, "A seed value can never be a pid".
 *
 * A boot script that fails is fatal, exactly as a failed seed is. The level
 * behind it would be a level whose subject is not running — the failure this
 * whole step exists to make impossible — and the one thing worse than
 * refusing to open it is opening it and grading it.
 */
/** How long a level's boot.sh may take before the level refuses to open. */
export const BOOT_TIMEOUT_MS = 110_000;
export async function bootWorkspace(ws, mission, seed) {
    if (mission.boot === undefined || mission.boot.trim() === '')
        return seed;
    // Under the guest agent's own 120 s backstop. It was 60 s, and the
    // node-server levels' boots measured 50-70 s in the real guest on a loaded
    // host (l58 failed to open alone at 64 s): the budget is what stands between
    // a slower laptop and a level that will not open. Boot scripts should still
    // poll with curl rather than a fresh `node` per attempt (docs/missions.md).
    const r = await ws.runScript(mission.boot, { env: seedEnv(seed), timeoutMs: BOOT_TIMEOUT_MS });
    if (r.code !== 0) {
        throw new Error(`boot script for "${mission.id}" failed (exit ${r.code}):\n${r.stderr || r.stdout}`);
    }
    return { ...seed, ...scrapeValues(r.stdout) };
}
/**
 * Shell that ends every process whose working directory is inside `root`.
 *
 * This is the other half of the boot step, and the reason the leash does not
 * belong in each level's own jobs. A workspace with processes in it is a
 * workspace that does not go away by being deleted: on Windows a directory
 * that is a live process's current directory cannot be removed at all, so
 * `LocalWorkspace.destroy` spends its five retries and gives up; and in the
 * browser there is one machine for the whole visit, so a job started for
 * level 52 is still running when level 53 extracts its tarball over the top
 * of it — two price syncs, one of which no level put there, and a check that
 * counts one of them failing for a reason no learner can see.
 *
 * The scope is the workspace directory, for the same reason a `shell` check
 * that looks for a process must scope itself to one (docs/missions.md,
 * "Processes are machine-global"): `npm test` runs missions concurrently, so
 * a sweep that matched on a command name would reach into the workspace
 * beside this one, or into an author's own editor.
 *
 * **It costs one process, not one per process.** This runs at the head of
 * every level load, in front of the learner, and the obvious shape — walk
 * /proc and `readlink` each entry's `cwd` — is a fork per process on a
 * machine where a fork is tens of milliseconds. Measured in the guest with
 * 69 processes, that loop was 3.1 s on a quiet host and 9.5 s on a busy one,
 * against 0.5 s of `tar` for the level it was holding up. One `find` asks the
 * same question of every process at once, and the two forks that are left are
 * the sentinel's and the one `readlink` per candidate below.
 *
 * `-printf` and `-lname` are GNU findutils, which is not a new dependency:
 * `listFiles` on both real drivers is already one `find -printf`, so a guest
 * without it has no file tree either. It is still not *assumed*, because the
 * way this fails is silent and the damage is a level graded against the
 * previous level's processes. `find` is asked to print `eos` for /proc
 * itself, which it can only do if `-printf` worked, and an answer that does
 * not start with it falls back to the fork-per-process loop.
 *
 * Five details are load-bearing:
 *
 *   - **It is a function, not a script.** The callers embed it in a larger
 *     one — loading a level in the browser is a single request — so it may
 *     not `exit`, and it may not run in a subshell either: a subshell's own
 *     working directory is inside the workspace, and it would sweep itself
 *     away mid-loop.
 *   - **Ancestors are never killed.** The guest agent's working directory is
 *     /workspace (sandbox/vm/eosd), so the naive sweep kills the process
 *     delivering the request and takes the whole tab with it. The parent
 *     chain is walked to pid 1 and everything on it is spared. It is walked
 *     with `read` and parameter expansion rather than `sed`, because a fork
 *     per generation is four or five more forks for an answer /proc has
 *     already written down. `stat` is stripped to its **last** `") "`: the
 *     second field is the command name, a process may be called `weird) x`,
 *     and the greedy strip is what the kernel's own readers do.
 *   - **What the sweep forks itself is not swept.** The pids are snapshotted
 *     with a glob before `find` runs, and anything not in that snapshot is
 *     spared. Without it the sweep finds its own command substitution and
 *     its own `find`, both standing in /workspace, and kills the request it
 *     is part of — which the fork-per-process loop avoided by accident,
 *     because its `for` glob was the snapshot. A job's own short-lived
 *     children are still caught, which is correct: their parent is going too.
 *   - **The learner's shell on ttyS0 is spared, and only that.** It is the
 *     one process here that something else already owns: inittab respawns it,
 *     and loading a level restarts it deliberately so that it lands in the new
 *     level's directory. Killing it here as well would leave inittab respawning
 *     a shell while that restart was still on its way, and the loser of that
 *     race is a prompt sitting in /workspace whose first `git` command answers
 *     "not a git repository". Nothing else is spared for having a terminal:
 *     the local driver kills its own ptys before it sweeps, and a job never
 *     has a terminal anyway — `start_job` takes its input from /dev/null and
 *     writes to a file, because a background child holding the seeding shell's
 *     pipe open would keep the seed itself alive.
 *
 * **SIGTERM, then SIGKILL.** A job that stops cleanly on TERM gets the
 * chance to, which is what a level's own jobs are written to do; the level 53
 * stray deliberately catches TERM and declines to leave, and nothing in a
 * workspace that is about to be deleted has earned a veto.
 *
 * It needs `/proc`, which the guest, the Docker image, Linux and Git Bash on
 * Windows all have (MSYS emulates it, `cwd` and `fd/` and all). macOS has no
 * `/proc`: the sweep finds nothing there and the local driver leaks whatever
 * boot started, which is written down here rather than papered over with an
 * `lsof` sweep that would also kill an editor with a file open under the
 * workspace.
 */
export function reapScript(root) {
    return `
__eos_reap() {
  local root keep seen found p d pid stdin doomed st
  root=$(cd ${q(root)} 2>/dev/null && pwd -P) || return 0
  [ -n "$root" ] || return 0

  keep=" $$ "
  p=$$
  while [ "$p" -gt 1 ] 2>/dev/null; do
    if [ -r "/proc/$p/ppid" ]; then
      read -r p < "/proc/$p/ppid" 2>/dev/null || true   # Cygwin, MSYS
    else
      st=
      read -r st < "/proc/$p/stat" 2>/dev/null || break  # Linux
      st=\${st##*") "}
      set -- $st
      p=\${2:-}
    fi
    case "$p" in '' | *[!0-9]*) break ;; esac
    keep="$keep$p "
  done

  seen=" "
  for d in /proc/[0-9]*; do seen="$seen\${d#/proc/} "; done

  found=$(find /proc /proc/[0-9]*/cwd -maxdepth 0 -name proc -printf 'eos ' -o -lname "$root" -printf '%h ' -o -lname "$root/*" -printf '%h ' 2>/dev/null)
  case "$found" in
    'eos '*) found=\${found#eos } ;;
    *)
      found=
      for d in /proc/[0-9]*; do
        case "$(readlink "$d/cwd" 2>/dev/null)" in "$root" | "$root"/*) found="$found$d " ;; esac
      done
      ;;
  esac

  doomed=
  for d in $found; do
    pid=\${d#/proc/}
    case "$keep" in *" $pid "*) continue ;; esac
    case "$seen" in *" $pid "*) ;; *) continue ;; esac
    stdin=$(readlink "$d/fd/0" 2>/dev/null)
    case "$stdin" in /dev/ttyS0) continue ;; esac
    doomed="$doomed $pid"
  done
  [ -n "$doomed" ] || return 0

  kill -TERM $doomed 2>/dev/null
  sleep 0.4
  kill -KILL $doomed 2>/dev/null
  return 0
}
__eos_reap
`;
}
/** The seeder the server and the author tests use. */
export class ScriptSeeder {
    seed(ws, mission) {
        return seedWorkspace(ws, mission);
    }
}
//# sourceMappingURL=seeder.js.map